import type { FormData } from '../../types.ts';
import type { ShopifyOfferDto } from '../../offer.types.ts';
import {
  type ApplyCouponRequestDto,
  type ApplyCouponResponseDto,
  CheckoutError,
  type ThankYouByTokenResponseDto,
  type UnifiedCheckoutResponseDto,
  type UnifiedCheckoutRequestDto,
} from './checkout.types.ts';

// V2 Function - Backend calculates prices using offerNanoId
export async function requestCouponApply(couponCode: string, formData: FormData): Promise<ApplyCouponResponseDto> {
  if (!couponCode || couponCode.trim().length === 0) {
    throw new CheckoutError('Código de cupom inválido ou vazio.', { couponCode });
  }

  // ✅ LÓGICA LIMPA: Backend calcula todos os preços usando offerNanoId
  const applyCouponRequest = {
    coupons: [],
    storeId: formData.shopifyOffer.store.nanoId ?? formData.shopifyOffer.storeId,
    offerNanoId: formData.shopifyOffer.nanoId,
    customerEmail: formData.userIdentification?.email?.trim() || undefined,
  } as ApplyCouponRequestDto;

  const url = `${import.meta.env.VITE_BACKEND_URL}/coupons/apply/${couponCode}`;
  // ✅ ATUALIZADO: Nova rota centralizada de cupons
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(applyCouponRequest),
  });

  const data = await response.json();

  if (!response.ok) {
    const details = { couponCode, url, statusText: response.statusText, cause: data };
    
    // Usar mensagem específica do backend se disponível
    const errorMessage = data?.message || data?.error?.message || `Erro ao aplicar o cupom. Status: ${response.status}`;
    
    if (data.error?.errorCode === 'COUPON_NOT_FOUND') {
      throw new CheckoutError('Cupom não encontrado.', details);
    }
    
    throw new CheckoutError(errorMessage, details);
  }

  const applyCouponResponse = data as ApplyCouponResponseDto;
  if (!applyCouponResponse.coupons || !applyCouponResponse.coupons.some(c => c.code === couponCode)) {
    throw new CheckoutError('Erro ao aplicar o cupom.', { couponCode, data: applyCouponResponse, url });
  }

  return applyCouponResponse;
}



/**
 * ✅ NOVA FUNÇÃO: Processa checkout usando dados do carrinho + pagamento
 * Fluxo: Carrinho + Dados do Cartão → Backend Unificado → Ações
 */
export async function checkoutPlaceOrderFromCart(
  cartCode: string,           // ← Receber cartCode ao invés de formData
  paymentData: any            // ← Receber apenas dados de pagamento
): Promise<UnifiedCheckoutResponseDto> {
  
  // 1. Buscar dados do carrinho
  const cartResponse = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}`);
  if (!cartResponse.ok) {
    throw new CheckoutError('Carrinho não encontrado');
  }
  const cartData = await cartResponse.json();
  
  // 2. Juntar dados do carrinho + pagamento
  const identificationDocument = String(cartData?.customerData?.document || '');
  const paymentDocument =
    paymentData?.paymentMethod === 'credit_card'
      ? String(paymentData?.creditCard?.cpf || identificationDocument)
      : paymentData?.paymentMethod === 'pix'
      ? String(paymentData?.pix?.cpf || identificationDocument)
      : paymentData?.paymentMethod === 'boleto'
      ? String(paymentData?.boleto?.cpf || identificationDocument)
      : identificationDocument;

  const unifiedData: UnifiedCheckoutRequestDto = {
    offerNanoId: cartData.offerNanoId,
    cartCode,
    payment: {
      method: paymentData.paymentMethod,
      // Prioriza CPF informado na etapa de pagamento; fallback para identificação.
      document: paymentDocument,
      creditCard: paymentData.paymentMethod === 'credit_card' ? (paymentData.creditCard?.cardNumber ? {
        number: paymentData.creditCard.cardNumber,
        holderName: paymentData.creditCard.cardholderName,
        expiryDate: paymentData.creditCard.expirationDate,
        cvv: paymentData.creditCard.cvv,
        installments: paymentData.creditCard.installments,
        cpf: paymentData.creditCard.cpf ?? paymentDocument,
      } : {
        holderName: paymentData.creditCard?.cardholderName || cartData.customerData?.name || '',
        expiryDate: '',
        cvv: '',
        installments: paymentData.creditCard?.installments ?? '1',
        cpf: paymentData.creditCard?.cpf || paymentDocument,
        number: '',
      }) : undefined,
    },
  };

  console.log('[CHECKOUT][DEBUG] unified request montado:', {
    cartCode,
    offerNanoId: unifiedData.offerNanoId,
    method: unifiedData.payment.method,
    documentLen: String(unifiedData.payment.document || '').replace(/\D/g, '').length,
    cartTotalPrice: cartData.totalPrice,
    cartItemsCount: Array.isArray(cartData.items) ? cartData.items.length : 0,
    cartOfferNanoId: cartData.offerNanoId,
  });
  
  // 3. Enviar para backend (mesma lógica da função antiga)
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/unified`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(unifiedData),
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    console.error('[CHECKOUT][DEBUG] unified HTTP erro:', {
      status: response.status,
      statusText: response.statusText,
      errorData,
    });
    throw new CheckoutError('Erro ao processar checkout', errorData);
  }
  
  const body = (await response.json()) as UnifiedCheckoutResponseDto;
  console.log('[CHECKOUT][DEBUG] unified response:', {
    success: body.success,
    orderId: body.orderId,
    transactionId: body.transactionId,
    gatewayUsed: (body as any).gatewayUsed,
    error: body.error,
    message: (body as any).message,
    hasPaymentData: Boolean(body.paymentData),
    hasPixEmv: Boolean(body.paymentData?.details?.emv || (body.paymentData as any)?.emv),
    finalPricing: body.finalPricing,
    pricing: body.pricing,
  });
  return body;
}

// Function to get updated prices by payment method
export async function requestPricesByPaymentMethod(
  offerUuid: string,
  paymentMethod: string,
  appliedCoupons?: string[],
  cartCode?: string,
): Promise<ShopifyOfferDto> {
  if (!offerUuid || !paymentMethod) {
    throw new CheckoutError('Dados inválidos para buscar preços por método de pagamento.', { offerUuid, paymentMethod });
  }

  // Build URL with optional query params
  const params = new URLSearchParams();
  if (appliedCoupons && appliedCoupons.length > 0) {
    params.set('coupons', appliedCoupons.join(','));
  }
  if (cartCode) {
    params.set('cartCode', cartCode);
  }
  const query = params.toString();
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/prices/${offerUuid}/${paymentMethod}${query ? `?${query}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new CheckoutError(`Erro ao buscar preços. Status: ${response.status}`, {
      offerUuid,
      paymentMethod,
      statusText: response.statusText,
      error: errorData
    });
  }

  const data = await response.json();

  return data;
}

// Fetch the checkout offer (same endpoint as initial load) — accepts optional cartCode
// so backend can merge accepted bumps into price/prices/items.
export async function fetchCheckoutOffer(
  offerUuid: string,
  cartCode?: string,
): Promise<ShopifyOfferDto> {
  if (!offerUuid) {
    throw new CheckoutError('offerUuid obrigatório para buscar oferta.', { offerUuid });
  }

  const query = cartCode ? `?cartCode=${encodeURIComponent(cartCode)}` : '';
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/offer/uuid/${offerUuid}${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new CheckoutError(`Erro ao buscar oferta. Status: ${response.status}`, {
      offerUuid,
      cartCode,
      statusText: response.statusText,
      error: errorData,
    });
  }

  const data = (await response.json()) as ShopifyOfferDto;
  return data;
}

/**
 * Processa pagamento adicional (3DS, análise, etc.)
 */
export async function processAdditionalPayment(
  orderId: string,
  paymentData: any,
  sessionToken?: string,
): Promise<UnifiedCheckoutResponseDto> {
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/unified/${orderId}/process-payment`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...paymentData, sessionToken }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new CheckoutError(`Erro ao processar pagamento adicional: ${response.status}`, {
      orderId,
      paymentData,
      statusText: response.statusText,
      cause: data
    });
  }

  return data as UnifiedCheckoutResponseDto;
}

/**
 * Finaliza Stripe Elements enviando o resultado do confirmCardPayment ao backend.
 * O backend processa via gateway e retorna resposta padronizada.
 */
export async function stripeElementsFinalize(params: {
  cartCode: string;
  sessionId: number;
  result: 'success' | 'error';
  transactionId?: string;
  stripeError?: { code?: string; decline_code?: string; message?: string };
}): Promise<{ success: boolean; orderId?: number; error?: string; code?: string; checkoutToken?: string }> {
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/stripe-elements/finalize`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data?.error || 'Erro ao processar resultado do pagamento.',
      code: data?.code,
    };
  }
  return data;
}

/**
 * Cobra o upsell one-click no mesmo cartão usado no pedido principal.
 * Reusa o token reutilizável `pgpm_` gerado pelo Payment Element da Pagou.
 * O backend valida orderId + token e cobra o valor do upsell.
 */
export async function chargeUpsell(params: {
  checkoutToken: string;
  orderId: string;
  upsellNanoId: string;
  reusableToken: string;
}): Promise<{ success: boolean; transactionId?: string | null; newOrderTotal?: number; error?: string }> {
  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/upsell/charge`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    return {
      success: false,
      error: data?.error || data?.message || 'Não foi possível processar a oferta adicional.',
    };
  }
  return data as { success: boolean; transactionId?: string | null; newOrderTotal?: number; error?: string };
}

export async function getThankYouByToken(token: string): Promise<ThankYouByTokenResponseDto> {
  const normalizedToken = String(token || '').trim();
  if (!normalizedToken) {
    throw new CheckoutError('Token de checkout inválido.');
  }

  const url = `${import.meta.env.VITE_BACKEND_URL}/checkout/thank-you/${encodeURIComponent(normalizedToken)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new CheckoutError('Token inválido ou expirado.', { token: normalizedToken, cause: data });
  }

  return data as ThankYouByTokenResponseDto;
}
